// @flow

/* eslint-disable ta-code-conventions/has-matching-storydoc */

// Skipping test coverage for this component since it is not possible to unit test correctly
/* eslint-disable ta-code-conventions/has-matching-test */
/* istanbul ignore file */

import { addTypenameToDocument } from 'apollo-utilities';
import deepEqual from 'fast-deep-equal';
import type { ApolloCache } from './Apollo';

type WatchOptions = {| query: any, variables: any, rootId: any, callback: any |};

// The real cache distinguishes between null and undefined variables, but our
// serialization method (JSON.stringify) doesn't support serializing undefined
// values. Thus, we have to replace it with this sentinel value during serialization,
// and then undo the transformation during deserialization.
const UNDEFINED_SENTINEL_VALUE = '__UNDEFINED_VAR__';

const replaceSentinels = obj => {
  Object.keys(obj).forEach(key => {
    if (obj[key] === UNDEFINED_SENTINEL_VALUE) {
      // eslint-disable-next-line no-param-reassign
      obj[key] = undefined;
    } else if (obj[key] !== null && typeof obj[key] === 'object') {
      replaceSentinels(obj[key]);
    }
  });
  return obj;
};

/**
 * A dummy cache backed by a map from query to response. No normalization is
 * performed, reading and writing fragments is unsupported, and watching for
 * changes simply queues your request for later. Saves 100+ ms on server-side
 * render of the social homepage.
 *
 * https://jira.tamg.io/browse/WP-763
 *
 * @author skline
 * @since 2018-10-09
 */
export default class DummyCache implements ApolloCache {
  cache: { query: any, variables: any, result: any }[] = [];
  transformedDocuments: Map<any, any> = new Map();
  watches: Array<?WatchOptions> = [];
  isDummy = true;

  extract(): any {
    return this.cache.map(entry => ({
      ...entry,
      variables: JSON.stringify(entry.variables || {}, (k, v) =>
        v === undefined ? UNDEFINED_SENTINEL_VALUE : v,
      ),
    }));
  }

  restore(entries: any) {
    if (entries) {
      this.cache.push(
        ...entries.map(entry => ({
          ...entry,
          variables: replaceSentinels(JSON.parse(entry.variables)),
        })),
      );
    }
  }

  read(options: any) {
    const entry = this.get(this.fixUpQuery(options));
    if (entry) return entry.result;
    throw new Error(`Read not satisfied by the cache.`);
  }

  write(options: any) {
    // Update the entry if it's already in the cache, otherwise add it to the cache
    const doc = this.fixUpQuery(options);
    const entry = this.get(doc);
    if (entry) {
      entry.result = options.result;
    } else {
      this.cache.push(doc);
    }
  }

  diff(options: any) {
    const entry = this.get(this.fixUpQuery(options));
    return { result: entry ? entry.result : null, complete: entry != null };
  }

  /**
   * Adds the __typename field to objects. This is the same transformation
   * used by Hermes as well as Apollo's default in-memory cache.
   */
  transformDocument(document: any) {
    if (this.transformedDocuments.has(document)) {
      return this.transformedDocuments.get(document);
    }
    if (!document.hasBeenTransformed) {
      const transformedDocument = addTypenameToDocument(document);
      transformedDocument.hasBeenTransformed = true;
      this.transformedDocuments.set(document, transformedDocument);
      return transformedDocument;
    }
    return document;
  }

  watch(options: WatchOptions) {
    // Don't record watches during SSR, since we don't support re-rendering
    if (SERVER) return () => {};

    // Record the watcher so that we can move it to the real cache later
    const index = this.watches.length;
    this.watches.push(this.fixUpQuery(options));
    return () => {
      this.watches[index] = null;
    };
  }

  getWatchers() {
    // $FlowFixMe: I just want to see if it's non-null
    return this.watches.filter(watcher => !!watcher);
  }

  getEntries() {
    return this.cache;
  }

  // eslint-disable-next-line class-methods-use-this
  readFragment() {
    return null;
  }

  get(options: any) {
    for (let i = 0; i < this.cache.length; i++) {
      const entry = this.cache[i];
      // The first condition is a quick short-circuit for non-matching queries
      if (
        entry.query.loc.source.body === options.query.loc.source.body &&
        deepEqual(entry.variables, options.variables) &&
        deepEqual(entry.query, options.query)
      ) {
        return entry;
      }
    }
    return null;
  }

  fixUpQuery(doc: any): any {
    return {
      ...doc,
      variables: doc.variables || {},
      query: this.transformDocument(doc.query),
    };
  }
}
